import React, { useState } from "react";

// Helpers
import { ScrollDisable } from "../../helpers/ScrollLock";

// Custom components
import OptionsModal from "../OptionsModal/OptionsModal";
import DeletionModal from "../OptionsModal/OptionsModalMenus/DeletionModal";
import EntryInfoModal from "../OptionsModal/OptionsModalMenus/EntryInfoModal";
import RapidEntryOptions from "../RapidEntryOptions/RapidEntryOptions";

// Icons
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

// Custom components
import TagAutoSuggest from "../TagAutoSuggest/TagAutoSuggest";
import { Heading1, Heading3 } from "../Headings/Headings";
import { SquareIconNavButton, TextActionButton } from "../Buttons/Buttons";
import {
  EntryRegularField,
  EntryTextAreaField,
} from "../InputFields/InputFields";
import AppointmentDateField from "../InputFields/AppointmentDateField";
import EntrySaveStatus from "../EntrySaveStatus/EntrySaveStatus";

function EntryScreenTemplate(props) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    children,
    entryTitle,
    handleMenuRedirect,
    appointmentDate,
    setAppointmentDate,
    suggestionList,
    tags,
    setTags,
    notes,
    setNotes,
    summary,
    setSummary,
    saveEntryFunction,
    deleteEntryFunction,
    entryChanged,
    entryData,
    getEntryData,

    // Rapid entry
    rapidEntry,
    rapidPatientUniqueId,
    setRapidPatientUniqueId,
    rapidPatientFullName,
    setRapidPatientFullName,
    selectedExistingPatientId,
    setSelectedExistingPatientId,
    setRapidEntryAction,
    // Field warnings.
    rapidPatientUniqueIdWarning,
    rapidPatientFullNameWarning,
  } = props;

  return (
    <div className="flex flex-col w-full pb-4 md:pb-0 Xbg-red-500">
      {deleteOpen && (
        <OptionsModal exitFunction={setDeleteOpen} setValue={false}>
          <DeletionModal
            warningTitle={"Delete Entry?"}
            warningParagraph={
              "You are about to delete this entry. This action cannot be undone."
            }
            confirmationSentence={"PERMANENTLY DELETE ENTRY"}
            cancelFunction={() => {
              setDeleteOpen(false);
            }}
            confirmFunction={deleteEntryFunction}
          />
        </OptionsModal>
      )}

      {infoOpen && (
        <OptionsModal exitFunction={setInfoOpen} setValue={false}>
          <EntryInfoModal
            entryPrompt={"Entry Details"}
            entryPromptDetails={"Technical information about entry."}
            entryData={entryData}
          />
        </OptionsModal>
      )}

      <div className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-2 items-center justify-between">
        <div className="flex flex-col flex-[1] mb-4 md:mb-0 md:flex-row space-y-2 md:space-y-0 md:space-x-2 w-full items-center justify-start Xbg-red-500">
          <SquareIconNavButton
            Icon={ArrowLeftIcon}
            actionFunction={handleMenuRedirect}
            activeColor={"active:bg-emerald-100"}
          />
          <EntrySaveStatus entryChanged={entryChanged} />
        </div>
        <div
          className="flex flex-col flex-[1] md:flex-row w-full items-center justify-end 
                     space-y-2 md:space-y-0 md:space-x-2
                     Xbg-blue-500"
        >
          {/* Appointment time */}
          <AppointmentDateField
            appointmentDate={appointmentDate}
            setAppointmentDate={setAppointmentDate}
          />
          <div className="flex flex-row space-x-4 items-center justify-center w-full md:w-auto Xbg-yellow-500">
            <SquareIconNavButton
              Icon={ArrowPathIcon}
              actionFunction={getEntryData}
              activeColor={"active:bg-cyan-100"}
            />

            <SquareIconNavButton
              Icon={InformationCircleIcon}
              actionFunction={() => {
                ScrollDisable();
                setInfoOpen(true);
              }}
              activeColor={"active:bg-purple-100"}
            />

            <SquareIconNavButton
              Icon={TrashIcon}
              actionFunction={() => {
                ScrollDisable();
                setDeleteOpen(true);
              }}
              activeColor={"active:bg-red-100"}
            />
          </div>
        </div>
      </div>

      <div className="py-2" />

      <Heading1 headingText={entryTitle} />

      <div className="py-2" />

      <div className="flex flex-col w-full Xbg-purple-500">{children}</div>

      <div className="py-4" />
      <Heading3 headingText={"Tags"} />
      <div className="py-1" />
      <TagAutoSuggest
        tags={tags}
        setTags={setTags}
        suggestionList={suggestionList}
      />

      <div className="py-4" />
      <Heading3 headingText={"Notes"} />
      <div className="py-1" />
      <EntryTextAreaField
        placeholder={"Detailed notes"}
        rowNumber={5}
        fieldValue={notes}
        setFieldValue={setNotes}
        maxLength={5000}
      />

      <div className="py-4" />
      <Heading3 headingText={"Brief Summary"} />
      <div className="py-1" />
      <EntryRegularField
        placeholder={"Brief summary for database"}
        fieldValue={summary}
        setFieldValue={setSummary}
        maxLength={50}
      />

      <div className="py-4" />

      {rapidEntry ? (
        <RapidEntryOptions
          saveEntryFunction={saveEntryFunction}
          setDeleteOpen={setDeleteOpen}
          rapidPatientUniqueId={rapidPatientUniqueId}
          setRapidPatientUniqueId={setRapidPatientUniqueId}
          rapidPatientFullName={rapidPatientFullName}
          setRapidPatientFullName={setRapidPatientFullName}
          selectedExistingPatientId={selectedExistingPatientId}
          setSelectedExistingPatientId={setSelectedExistingPatientId}
          setRapidEntryAction={setRapidEntryAction}
          // Field warnings.
          rapidPatientUniqueIdWarning={rapidPatientUniqueIdWarning}
          rapidPatientFullNameWarning={rapidPatientFullNameWarning}
        />
      ) : (
        <div className="flex flex-row items-center justify-end w-full Xbg-red-500">
          <TextActionButton
            prompt={"Save Entry"}
            actionFunction={saveEntryFunction}
          />
        </div>
      )}
    </div>
  );
}

export default EntryScreenTemplate;
